/** @jsx tsx */
import * as _ from "lodash";
import { w, tsx } from "@dojo/framework/core/vdom";
import { DNode } from "@dojo/framework/core/interfaces";
import { ConnectionShapeContainer, ShapeContainer } from "../containers/ShapeContainer";
import { IPageObjectTypes } from "../interfaces/model/enumerations/PageObjectTypes";
import { IGroupShape } from "../interfaces/model/PageObjects/GroupShape";
import { IDragResults } from "../meta/Drag/index";
import { IPageObjectRenderProps, PageObject } from "./PageObject";
import { Picture } from "./Picture";
import { ActionCreator } from "redux";
import { xor, getStartTypeForKey } from "../../store/reducers/util";
import { IExtents } from "../interfaces/model/dataStructures";
import { IShape } from "../interfaces/model/PageObjects";
import { SelectionConstants } from "../../store/reducers/index";
import { isShapeObject, isConnectionShapeObject, isPictureObject } from "../util";
import reflection from "csi/reflection";

/**
 * Group shape renderer properties
 */
export interface IGroupShapeRenderProps extends IPageObjectRenderProps<IGroupShape> {
	absoluteFlipH?: boolean;

	absoluteFlipV?: boolean;

	absoluteLock?: boolean;

	/** Flag used for generating canvas snapshots */
	dontDownloadImages?: boolean;

	/** image load event */
	emitImagesLoadedEvent(): void;

	/** Index of selected element in group */
	groupSelectionIndices: number[];

	/** Type of objects to be highlighted. */
	highlightType: string;

	/** List of attribute keys in the canvas that cannot be filled */
	impossiblePaths?: string[];

	/** Callback to set first character styles in state */
	setFirstCharStyle?: ActionCreator<any>;

	/** Callback to set selected shape extends */
	setObjectExtends(extend: { cx?: number; cy?: number }, transient?: boolean): void;

	/** Callback to set shape text content */
	setShapeTextContent?: ActionCreator<any>;

	/** Callback to set source rectangle refExt */
	setSrcRectRefExt(refExt: IExtents): void;

	/** Callback to set text editor instance */
	setTextEditorInstance?: ActionCreator<any>;
}

/**
 * Group stateless renderer
 */
export class GroupShape extends PageObject<IGroupShapeRenderProps> {
	/**
	 * For group shape, this method will not have implementation as not required
	 */
	protected renderObject(): DNode {
		throw new Error("Method not implemented.");
	}

	/**
	 * For group shape, this method will not have implementation as not required
	 */
	protected renderIfShapeTextBody(): DNode {
		throw new Error("Method not implemented.");
	}

	/**
	 * Select group shape.  If group is already selected, select a subgroup item.
	 * @param childKey {Number} child object key
	 * @param isMultiSelect {boolean} if true, multiple objects to be selected
	 * @param noDrillIn {boolean} whether to force drilling in to select sub elements
	 */
	protected groupSelect(childKey: number | string, isMultiSelect?: boolean, noDrillIn?: boolean) {
		const drillIn = !noDrillIn || this.properties.groupSelectionIndices.length > 0;
		if (drillIn && this.properties.isSelected && typeof this.properties.key === "number" && !this.properties.pageObject.spPr.locked) {
			if (typeof childKey === "string") {
				const childId = parseInt(childKey.split("_")[1]!, 10);
				this.properties.selectObject(this.properties.key as number, isMultiSelect, true, childId);
			}
		} else {
			this.properties.selectObject(this.properties.key as number, isMultiSelect);
		}
	}

	/**
	 * Translate group object.
	 * @param drag {IDragResults}
	 * @param transient {boolean}
	 */
	protected groupTranslate(drag: IDragResults, transient: boolean) {
		if (this.properties.translateObjects) {
			this.properties.translateObjects(drag, transient);
		}
	}

	/**
	 * Get HTML Element of current Page Object
	 * @param key
	 * @return {HTMLElement} pageObject
	 */
	protected getGroupObjectElement(key: string | number | undefined): HTMLElement {
		return this.properties.canvasContainerEl.querySelector("#pageObject_matrix_" + key) as HTMLElement;
	}


	/**
	 * Return true if this group is highlighted and can be dragged over
	 * @param pageObject - current page object
	 */
	protected groupObjectIsHighlighted(pageObject: IGroupShape): boolean {
		if (pageObject.childObjects) {
			const childObject = pageObject.childObjects[0] as IShape;
			const { attributeKey } = childObject;
			const { highlightType } = this.properties;
			if (attributeKey && highlightType && highlightType != "ImagePlaceholder") {
				const attributeKeyType = getStartTypeForKey(attributeKey);
				const hc = reflection.getClass(highlightType);
				return hc.isDerivedFrom(attributeKeyType);
			}
		}
		return false;
	}

	/**
	 * Whether the rendered shape should be highlighted or not.
	 */
	protected groupObjectHighlightOn(): void {
		const {
			key,
			pageObject,
			absoluteLock,
		} = this.properties;
		const el = this.getGroupObjectElement(key);
		const { style } = el;
		if (absoluteLock || pageObject.spPr.locked) {
			style.outline = SelectionConstants.DEFAULT_HIGHLIGHT_RED;
		} else {
			style.outline = SelectionConstants.DEFAULT_HIGHLIGHT_BLUE;
		}
	}

	/**
	 * Whether the rendered shape should be highlighted or not.
	 */
	protected groupObjectHighlightOff(): void {
		const {
			key,
		} = this.properties;
		const el = this.getGroupObjectElement(key);
		const { style } = el;
		style.outline = SelectionConstants.DEFAULT_HIGHLIGHT_GREY;
	}

	/**
	 * Translate group object.
	 */
	protected groupMouseEnter(event: MouseEvent) {
		if (this.groupObjectIsHighlighted(this.properties.pageObject)) {
			this.properties.removePageDragOverClass();
			this.groupObjectHighlightOn();
		}
	}

	/**
	 * Translate group object.
	 */
	protected groupMouseLeave(event: MouseEvent) {
		if (this.groupObjectIsHighlighted(this.properties.pageObject)) {
			this.properties.addPageDragOverClass();
			this.groupObjectHighlightOff();
		}
	}

	protected renderImpossibleMask() {
		let renderMask = false;

		if (!this.properties.impossiblePaths) {
			return;
		}

		_.each(this.properties.pageObject.childObjects, (object) => {
			const po = object as IShape;
			if (po.attributeKey !== undefined) {
				_.each(this.properties.impossiblePaths, (type) => {
					if (getStartTypeForKey(po.attributeKey!) === type) {
						renderMask = true;
					}
				});
			}
		});

		if (renderMask) {
			return (
				<div
					styles={
						{
							display: "inline-block",
							left: "0px",
							pointerEvents: "none",
							position: "absolute",
							backgroundColor: "red",
							opacity: "0.4",
							top: "0px",
							zIndex: "10",
							width: "100%",
							height: "100%",
						}}
				/>
			);
		}
	}

	/**
	 * Render Group shape.
	 * Overrides PageObject.
	 * Render group shape components.
	 * For group shape, manipulates children objects.
	 *
	 * @returns Array of child DNodes
	 */
	protected _renderComponent(): DNode | DNode[] {
		const { flipH, flipV } = this.properties.pageObject.spPr.xfrm;
		let { rot } = this.properties.pageObject.spPr.xfrm;
		const {
			addPageDragOverClass,
			applyFormatPainter,
			canvasContainerEl,
			canvasMode,
			emitImagesLoadedEvent,
			highlightType,
			impossiblePaths,
			removePageDragOverClass,
		} = this.properties;

		const useFlipH = xor(flipH, !!this.properties.absoluteFlipH);
		const useFlipV = xor(flipV, !!this.properties.absoluteFlipV);

		let scale = "";
		if (flipH) {
			scale = "scale(-1, 1)";
		}
		rot = rot || 0;
		const { childObjects } = this.properties.pageObject;
		const partOfGroup: boolean = true;
		const depth = this.properties.depth + 1;
		const absoluteLock = this.properties.absoluteLock || this.properties.pageObject.spPr.locked;

		let outline = "";
		if (this.groupObjectIsHighlighted(this.properties.pageObject)) {
			outline = SelectionConstants.DEFAULT_HIGHLIGHT_GREY;
		}

		let poMatrix = `pageObject_matrix_${this.properties.key}`;
		if (this.properties.dontDownloadImages) {
			poMatrix = `ddipageObject_matrix_${this.properties.key}`;
		}

		const pos = childObjects.map((pageObject, key) => {
			if (isShapeObject(pageObject)) {
				return (
					<ShapeContainer
						absoluteLock={absoluteLock}
						addPageDragOverClass={addPageDragOverClass}
						applyFormatPainter={applyFormatPainter}
						canvasContainerEl={canvasContainerEl}
						canvasMode={canvasMode}
						defaultTextStyles={this.properties.defaultTextStyles}
						depth={depth}
						deselectAllObjects={this.properties.deselectAllObjects}
						dontDownloadImages={this.properties.dontDownloadImages}
						editPageObject={this.properties.editPageObject}
						emitImagesLoadedEvent={emitImagesLoadedEvent}
						groupShapeKey={`${this.properties.key}_${key}`}
						highlightType={highlightType}
						isSelected={this.properties.isSelected && _.indexOf(this.properties.groupSelectionIndices, key) !== -1}
						key={`${this.properties.key}_${key}`}
						pageObject={pageObject}
						parentFlipH={useFlipH}
						parentFlipV={useFlipV}
						parentGroupIsSelected={this.properties.isSelected}
						partOfGroup={partOfGroup}
						removePageDragOverClass={removePageDragOverClass}
						selectObject={this.groupSelect}
						selectObjectById={this.properties.selectObjectById}
						selectedPageId={this.properties.selectedPageId}
						setFirstCharStyle={this.properties.setFirstCharStyle}
						setObjectExtends={this.properties.setObjectExtends}
						setShapeTextContent={this.properties.setShapeTextContent}
						setSrcRectRefExt={this.properties.setSrcRectRefExt}
						setTextEditorInstance={this.properties.setTextEditorInstance}
						subgroupSelect={this.properties.selectObject}
						translateObjects={this.groupTranslate}
					/>
				);
			}

			if (isConnectionShapeObject(pageObject)) {
				return (
					<ConnectionShapeContainer
						absoluteLock={absoluteLock}
						applyFormatPainter={applyFormatPainter}
						canvasMode={canvasMode}
						depth={depth}
						editPageObject={this.properties.editPageObject}
						key={`${this.properties.key}_${key}`}
						pageObject={pageObject}
						parentGroupIsSelected={this.properties.isSelected}
						partOfGroup={partOfGroup}
						selectObject={this.groupSelect}
						selectObjectById={this.properties.selectObjectById}
						translateObjects={this.groupTranslate}
					/>
				);
			}

			if (isPictureObject(pageObject)) {
				return (
					<Picture
						absoluteLock={absoluteLock}
						addPageDragOverClass={addPageDragOverClass}
						applyFormatPainter={applyFormatPainter}
						canvasContainerEl={canvasContainerEl}
						canvasMode={canvasMode}
						depth={depth}
						editPageObject={this.properties.editPageObject}
						key={`${this.properties.key}_${key}`}
						pageObject={pageObject}
						parentGroupIsSelected={this.properties.isSelected}
						partOfGroup={partOfGroup}
						removePageDragOverClass={removePageDragOverClass}
						selectObject={this.groupSelect}
						selectObjectById={this.properties.selectObjectById}
						setObjectExtends={this.properties.setObjectExtends}
						translateObjects={this.groupTranslate}
					/>
				);
			}

			return pageObject.childObjects.length ? (
				<GroupShape
					absoluteFlipH={useFlipH}
					absoluteFlipV={useFlipV}
					absoluteLock={absoluteLock}
					addPageDragOverClass={addPageDragOverClass}
					applyFormatPainter={applyFormatPainter}
					canvasContainerEl={canvasContainerEl}
					canvasMode={canvasMode}
					depth={depth}
					emitImagesLoadedEvent={emitImagesLoadedEvent}
					groupSelectionIndices={[]}
					highlightType={highlightType}
					impossiblePaths={impossiblePaths}
					key={`${this.properties.key}_${key}`}
					pageObject={pageObject}
					parentGroupIsSelected={this.properties.isSelected}
					partOfGroup={partOfGroup}
					removePageDragOverClass={removePageDragOverClass}
					selectObject={this.groupSelect}
					selectObjectById={this.properties.selectObjectById}
					setObjectExtends={this.properties.setObjectExtends}
					setSrcRectRefExt={this.properties.setSrcRectRefExt}
					translateObjects={this.groupTranslate}
				/>
			) : null;
		});

		return (
			<div
				id={poMatrix}
				key={poMatrix}
				onmouseenter={this.groupMouseEnter}
				onmouseleave={this.groupMouseLeave}
				styles={{
					outline: `${outline}`,
					height: `${this.properties.pageObject.spPr.xfrm.ext.cy}px`,
					pointerEvents: "none",
					transform: `rotate(${rot}deg) ${scale}`,
					transformOrigin: "center",
					width: `${this.properties.pageObject.spPr.xfrm.ext.cx}px`,
				}}
			>
				{pos}
				{this.renderImpossibleMask()}
			</div>
		);
	}
}

export default GroupShape;
